// frontend/src/components/sps/BasketballLoader.jsx
// Animated basketball player shown while an import is running.
// Drop this in place of (or alongside) the DropZone when isRunning is true.
// Usage: <BasketballLoader isRunning={isLoading} elapsed={elapsed} />

import { useEffect, useRef } from 'react';

export default function BasketballLoader({ isRunning, elapsed = 0 }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef({
    phase: 'hold', t: 0, ballSpin: 0, totalElapsed: 0,
    score: 0, swishAlpha: 0, netSway: 0, bounceN: 0,
    confetti: [], lastTime: 0, rafId: null,
  });

  useEffect(() => {
    if (!isRunning) {
      if (stateRef.current.rafId) cancelAnimationFrame(stateRef.current.rafId);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = 480, H = 320;
    canvas.width = W; canvas.height = H;

    const S = stateRef.current;
    S.phase = 'hold'; S.t = 0; S.ballSpin = 0; S.totalElapsed = 0;
    S.score = 0; S.swishAlpha = 0; S.netSway = 0; S.bounceN = 0;
    S.confetti = [];

    const FLOOR_Y = 248, HOOP_X = 360, HOOP_Y = 128, HOOP_R = 20;
    const PLAYER_X = 90;

    const crowd = Array.from({length: 24}, (_, i) => ({
      x: 28 + (i % 12) * 34 + Math.floor(i/12) * 8,
      y: 24 + Math.floor(i/12) * 22,
      color: ['#e05252','#52b8e0','#e0c052','#a052e0','#52e07a'][i % 5],
      phase: Math.random() * Math.PI * 2,
    }));

    function throwArc(tt) {
      const sx = PLAYER_X+10, sy = FLOOR_Y-105;
      const ex = HOOP_X-4,    ey = HOOP_Y+5;
      const cx = (sx+ex)/2-18, cy = Math.min(sy,ey)-90;
      return {
        x: (1-tt)*(1-tt)*sx + 2*(1-tt)*tt*cx + tt*tt*ex,
        y: (1-tt)*(1-tt)*sy + 2*(1-tt)*tt*cy + tt*tt*ey,
      };
    }

    function limbs(ph, tt) {
      if (ph === 'hold') return { rA:-0.3, lA:-0.5, rL:0.05, lL:-0.05, lean:0, jump:0 };
      if (ph === 'throw') {
        if (tt < 0.35) {
          const w = tt/0.35;
          return { rA:-0.3+w*1.2, lA:-0.5+w*0.3, rL:0.05+w*0.15, lL:-0.05-w*0.1, lean:-w*0.18, jump:0 };
        }
        const w = (tt-0.35)/0.65;
        return { rA:0.9-w*1.4, lA:-0.2-w*0.3, rL:0.2-w*0.15, lL:-0.15+w*0.05, lean:-0.18+w*0.22, jump:Math.sin(w*Math.PI)*20 };
      }
      if (ph === 'scored') return { rA:-1.1, lA:-1.0, rL:0.18, lL:-0.1, lean:0, jump:0, cel:true };
      return { rA:-0.3, lA:-0.5, rL:0.05, lL:-0.05, lean:0, jump:0 };
    }

    function draw(ts) {
      const dt = Math.min((ts - S.lastTime)/1000, 0.05);
      S.lastTime = ts; S.totalElapsed += dt;
      ctx.clearRect(0,0,W,H);

      // BG
      ctx.fillStyle = '#1a1a2e'; ctx.fillRect(0,0,W,H);
      ctx.fillStyle = '#2a2050'; ctx.fillRect(0,0,W,55);
      ctx.fillStyle = '#1e1840'; ctx.fillRect(0,47,W,16);

      // Crowd
      crowd.forEach(p => {
        const bob = Math.sin(S.totalElapsed*3+p.phase)*2.5;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.ellipse(p.x,p.y+bob,6,8,0,0,Math.PI*2); ctx.fill();
        ctx.fillStyle = '#e8c89a';
        ctx.beginPath(); ctx.arc(p.x,p.y-9+bob,4.5,0,Math.PI*2); ctx.fill();
      });

      // Floor
      ctx.fillStyle = '#2a1f0a'; ctx.fillRect(0,FLOOR_Y,W,H-FLOOR_Y);
      ctx.strokeStyle = '#3a2d10'; ctx.lineWidth = 1;
      for (let i=0;i<10;i++) { ctx.beginPath(); ctx.moveTo(0,FLOOR_Y+8+i*6); ctx.lineTo(W,FLOOR_Y+8+i*6); ctx.stroke(); }
      ctx.strokeStyle = '#6a5020'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(PLAYER_X+75,FLOOR_Y,52,Math.PI,0); ctx.stroke();

      // Pole + board
      ctx.fillStyle = '#888'; ctx.fillRect(412,HOOP_Y+8,5,FLOOR_Y-HOOP_Y-8);
      ctx.fillStyle = '#ccd'; ctx.fillRect(392,HOOP_Y-30,10,50);
      ctx.strokeStyle = '#99a'; ctx.lineWidth=1.5; ctx.strokeRect(392,HOOP_Y-30,10,50);
      ctx.strokeStyle='#e05252'; ctx.strokeRect(393,HOOP_Y-15,8,18);

      // Hoop
      ctx.strokeStyle='#e05252'; ctx.lineWidth=3.5;
      ctx.beginPath(); ctx.ellipse(HOOP_X,HOOP_Y,HOOP_R,5,0,0,Math.PI*2); ctx.stroke();
      // Net
      const sw = S.netSway * Math.exp(-S.totalElapsed*0.5);
      ctx.strokeStyle='rgba(240,230,200,0.65)'; ctx.lineWidth=0.8;
      for (let i=0;i<=8;i++) {
        const a = (i/8)*Math.PI*2;
        ctx.beginPath();
        ctx.moveTo(HOOP_X+Math.cos(a)*HOOP_R, HOOP_Y+Math.sin(a)*4+2);
        ctx.lineTo(HOOP_X+sw*5+Math.cos(a)*HOOP_R*0.32, HOOP_Y+32+Math.sin(a)*2.5);
        ctx.stroke();
      }
      for (let r=0;r<4;r++) {
        const frac=(r+1)/5, ry=HOOP_Y+2+frac*30, rad=HOOP_R*(1-frac*0.68);
        ctx.beginPath(); ctx.ellipse(HOOP_X+sw*5*frac,ry,rad,rad*0.28,0,0,Math.PI*2); ctx.stroke();
      }

      // Player
      const L = limbs(S.phase, S.t);
      const baseY = FLOOR_Y - (L.jump||0);
      ctx.save(); ctx.translate(PLAYER_X, baseY); ctx.rotate(L.lean||0);
      [[L.rL,1,true],[L.lL,-1,false]].forEach(([a,sg]) => {
        ctx.save(); ctx.translate(sg*5,-34); ctx.rotate(a);
        ctx.fillStyle='#e05252'; ctx.fillRect(-4,0,8,20);
        ctx.fillStyle='#222'; ctx.fillRect(-3,18,7,18);
        ctx.fillStyle='#fff'; ctx.beginPath(); ctx.ellipse(0,38,9,4,0,0,Math.PI*2); ctx.fill();
        ctx.restore();
      });
      ctx.fillStyle='#1a3a8f'; ctx.beginPath(); ctx.roundRect(-12,-78,24,44,3); ctx.fill();
      ctx.fillStyle='#fff'; ctx.font='bold 13px sans-serif'; ctx.textAlign='center'; ctx.fillText('23',0,-56);
      [[L.rA,1],[L.lA,-1]].forEach(([a,sg]) => {
        ctx.save(); ctx.translate(sg*12,-72); ctx.rotate(a*sg);
        ctx.fillStyle='#1a3a8f'; ctx.fillRect(-3,0,7,20);
        ctx.fillStyle='#e8a86a'; ctx.fillRect(-3,18,7,14);
        ctx.beginPath(); ctx.arc(0,34,6,0,Math.PI*2); ctx.fill();
        ctx.restore();
      });
      ctx.fillStyle='#e8a86a'; ctx.beginPath(); ctx.arc(0,-90,14,0,Math.PI*2); ctx.fill();
      ctx.strokeStyle='#e05252'; ctx.lineWidth=2.5;
      ctx.beginPath(); ctx.arc(0,-90,14,-Math.PI*0.7,Math.PI*0.4); ctx.stroke();
      ctx.fillStyle='#333';
      ctx.beginPath(); ctx.arc(-4,-91,1.8,0,Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(4,-91,1.8,0,Math.PI*2); ctx.fill();
      if (L.cel) { ctx.font='20px sans-serif'; ctx.fillText('🎉',0,-112); }
      ctx.restore();

      // Ball position
      let bx = PLAYER_X+10, by = FLOOR_Y-100;
      const HOLD_DUR=1.1, THROW_DUR=0.65, FALL_DUR=0.42, SCORE_DUR=0.9, BOUNCE_DUR=0.65;

      if (S.phase==='hold') {
        bx=PLAYER_X+10; by=FLOOR_Y-100; S.ballSpin+=dt*1.5;
        S.t+=dt/HOLD_DUR; if(S.t>=1){S.phase='throw';S.t=0;}
      } else if (S.phase==='throw') {
        const p=throwArc(Math.min(S.t,1)); bx=p.x; by=p.y; S.ballSpin+=dt*8;
        S.t+=dt/THROW_DUR; if(S.t>=1){S.phase='falling';S.t=0;}
      } else if (S.phase==='falling') {
        bx=HOOP_X-4+S.t*5; by=HOOP_Y+4+S.t*(FLOOR_Y-HOOP_Y-14); S.ballSpin+=dt*5;
        S.t+=dt/FALL_DUR;
        if(S.t>=1){ S.score+=2; S.netSway=1; S.totalElapsed=0; S.swishAlpha=1.3;
          for(let i=0;i<20;i++) S.confetti.push({x:HOOP_X+(Math.random()-.5)*35,y:HOOP_Y-8,
            vx:(Math.random()-.5)*4.5,vy:-3.5-Math.random()*3.5,
            color:['#e05252','#52b8e0','#e0c052','#a052e0','#52e07a'][i%5],
            life:1,w:5+Math.random()*4,h:3+Math.random()*3,rot:Math.random()*Math.PI});
          S.phase='scored';S.t=0;S.bounceN=0; }
      } else if (S.phase==='scored') {
        bx=HOOP_X; by=FLOOR_Y-14; S.ballSpin+=dt*2; S.swishAlpha-=dt*1.3;
        S.t+=dt/SCORE_DUR; if(S.t>=1){S.phase='bounce';S.t=0;}
      } else if (S.phase==='bounce') {
        const up=Math.abs(Math.sin(S.t*Math.PI*2.5));
        const decay=Math.exp(-S.bounceN*0.85)*32;
        bx=HOOP_X+S.t*(PLAYER_X+10-HOOP_X); by=FLOOR_Y-14-up*decay; S.ballSpin+=dt*6;
        S.t+=dt/BOUNCE_DUR;
        if(S.t>=1){ S.bounceN++; if(S.bounceN>=2){S.phase='hold';S.t=0;}else{S.t=0;} }
      }

      // Confetti
      S.confetti=S.confetti.filter(c=>c.life>0);
      S.confetti.forEach(c=>{
        ctx.save(); ctx.globalAlpha=c.life; ctx.translate(c.x,c.y); ctx.rotate(c.rot);
        ctx.fillStyle=c.color; ctx.fillRect(-c.w/2,-c.h/2,c.w,c.h); ctx.restore();
        c.x+=c.vx; c.y+=c.vy; c.vy+=0.17; c.rot+=0.1; c.life-=dt*1.3;
      });

      // Ball shadow
      const shSz=Math.max(3,14-(FLOOR_Y-by)*0.04);
      ctx.fillStyle='rgba(0,0,0,0.18)';
      ctx.beginPath(); ctx.ellipse(bx,FLOOR_Y+1,shSz,3.5,0,0,Math.PI*2); ctx.fill();

      // Ball
      ctx.save(); ctx.translate(bx,by); ctx.rotate(S.ballSpin);
      const g=ctx.createRadialGradient(-4,-4,1,0,0,11);
      g.addColorStop(0,'#f5a040'); g.addColorStop(1,'#c55800');
      ctx.fillStyle=g; ctx.beginPath(); ctx.arc(0,0,11,0,Math.PI*2); ctx.fill();
      ctx.strokeStyle='#7a3000'; ctx.lineWidth=1.1;
      ctx.beginPath(); ctx.arc(0,0,11,0.3,Math.PI-0.3); ctx.stroke();
      ctx.beginPath(); ctx.arc(0,0,11,Math.PI+0.3,-0.3); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0,-11); ctx.bezierCurveTo(6.5,-3,6.5,3,0,11); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0,-11); ctx.bezierCurveTo(-6.5,-3,-6.5,3,0,11); ctx.stroke();
      ctx.restore();

      // Swish
      if (S.swishAlpha > 0) {
        ctx.save(); ctx.globalAlpha=Math.min(1,S.swishAlpha);
        ctx.fillStyle='#ffd700'; ctx.font='bold 20px sans-serif'; ctx.textAlign='center';
        ctx.fillText('SWISH! 🏀', HOOP_X-16, HOOP_Y-44); ctx.restore();
      }

      // Score board
      ctx.fillStyle='rgba(0,0,0,0.5)'; ctx.beginPath(); ctx.roundRect(W/2-40,8,80,28,5); ctx.fill();
      ctx.fillStyle='#fff'; ctx.font='bold 13px sans-serif'; ctx.textAlign='center';
      ctx.fillText(`🏀 ${S.score} pts`, W/2, 27);

      S.rafId = requestAnimationFrame(draw);
    }

    S.rafId = requestAnimationFrame(ts => { S.lastTime = ts; requestAnimationFrame(draw); });
    return () => { if (S.rafId) cancelAnimationFrame(S.rafId); };
  }, [isRunning]);

  if (!isRunning) return null;

  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', margin: '1rem 0', background: '#1a1a2e' }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%' }} />
    </div>
  );
}
